import { Component, Inject, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  CertStatus, ICertificateData, ICertificateDetailsData,
  PackType, personalIdType, planName, statusName
} from '@app/certificates/manage/manage';
import { ActionCertificatesInitialize, selectorCertificate } from '@app/certificates/manage/manage.reducer';
import { ManageService } from '@app/certificates/manage/manage.service';
import { InfoService } from '@app/core/info/info.service';
import { ISpringResponseBody } from '@app/shared/interfaces/response-interfaces';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';

export class AliasErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
  selector: 'cloud-certificate-details',
  templateUrl: './certificate-details.component.html',
  styleUrls: ['./certificate-details.component.scss']
})
export class CertificateDetailsComponent implements OnInit, OnDestroy {

  @Output() close: EventEmitter<any> = new EventEmitter();

  private lang: string;
  wait = true;
  private unsubscribe$: Subject<void> = new Subject<void>();

  private certificate: ICertificateDetailsData;
  private certificates: ICertificateData[];

  readonly dateFormat = environment.dateFormat;
  matcher = new AliasErrorStateMatcher();

  constructor(
    private translateService: TranslateService,
    private manageService: ManageService,
    private store: Store<any>,
    private info: InfoService,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    this.manageService.getCertDetails(data.certId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        this.certificate = response.data;
        this.wait = false;
      });
    this.lang = this.translateService.currentLang;
    this.store.select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((d) => this.certificates = d);
  }

  ngOnInit() {
  }

  statusName(status: CertStatus): string {
    return statusName[status] && statusName[status].text ? statusName[status].text : '';
  }

  planName(type: PackType): string {
    return planName[type].text;
  }

  personalIdLabel(personalId: string) {
    const elements = personalId.split('-');
    let prefix = '';
    if (elements.length > 0) {
      prefix = elements[0].substr(0, 3);
      if (prefix.indexOf('PI:') !== -1) {
        return personalIdType.PNO.text;
      }
    }
    return personalIdType[prefix].text;
  }

  personalIdValue(personalId: string) {
    const elements = personalId.split('-');
    let suffix = '';
    if (elements.length > 1) {
      suffix = elements[1];
    }
    return suffix;
  }

  isInfinite(cert: ICertificateDetailsData): boolean {
    return cert.packType === PackType.INFINITE ? true : false;
  }

  onSaveAlias() {
    this.manageService.updateCertAlias(this.certificate.certId.toString(), this.certificate.alias)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: ISpringResponseBody) => {
        if (res.status === 200) {
          if (this.certificates instanceof Array) {
            const certificatesCopy = this.certificates.slice(0);
            const index = certificatesCopy.findIndex((c) => c.certId === this.certificate.certId);
            certificatesCopy[index] = { ...this.certificates[index], alias: this.certificate.alias };
            this.store.dispatch(new ActionCertificatesInitialize({ certificates: certificatesCopy }));
            this.closeDialog();
            this.info.success(res.message);
          }
        } else {
          this.info.error(res.message);
        }
      });
  }

  get aliasPattern() {
    return '^[^\/]*$';
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  closeDialog() {
    this.close.emit(null);
  }
}
